import { CSSResultGroup, TemplateResult, html } from 'lit';
import { OutlineElement } from '../../base/outline-element/outline-element';
import {
  customElement,
  property,
  queryAssignedElements,
  query,
} from 'lit/decorators.js';
import componentStyles from './sic-main-header.css.lit';
import {
  screenSizeMap,
  ScreenSizeController,
} from '../../controllers/screen-size-controller';
import { classMap } from 'lit/directives/class-map.js';
import '../sic-container/sic-container';

import { debounce } from '../../../utilities/debounce';
import { SicNavItem } from '../sic-nav-menu/sic-nav-item/sic-nav-item';
import { SicFlyout } from '../sic-nav-menu/sic-flyout/sic-flyout';
/**
 * The SicMainHeader component
 * @element sic-main-header
 */
@customElement('sic-main-header')
export class SicMainHeader extends OutlineElement {
  static styles: CSSResultGroup = [componentStyles];
  screens = new ScreenSizeController(this);
  resizeObserver: ResizeObserver;
  stickyPositionObserver = new IntersectionObserver(
    this.toggleFixedClass.bind(this)
  );

  @property({ type: Boolean, attribute: 'sticky-menu' })
  stickyMenu = false;

  @property({ type: Boolean })
  menuOpen = false;

  @property({ type: Number })
  scrollY = 0;

  @property({ type: String })
  menuHiddenPosition = `-123px`;

  @queryAssignedElements({ slot: 'main-menu' })
  mainMenu: Array<HTMLElement>;

  @query('.sic-main-header')
  main: HTMLElement;

  @property({ type: Boolean })
  isMobile = window.innerWidth < (screenSizeMap.get('lg') as number);

  @property({ type: Number })
  alertHeight = 0;

  /**
   * Prevents the menu reveal on scroll (hidden position remains)
   */
  @property({ type: Boolean, reflect: true })
  haltScrollShow = false;

  connectedCallback() {
    super.connectedCallback();
    this.resizeObserver = new ResizeObserver(() => {
      this.resetAtScreen();
    });
    // add escape handler to window
    window.addEventListener('keydown', (e: KeyboardEvent) =>
      this.handleEscape(e)
    );
    // add window specific handler to close open elements when clicking away
    window.addEventListener('click', (e: Event) =>
      this.windowClickAwayHandler(e)
    );
    // add scroll behavior to window.
    window.addEventListener(
      'scroll',
      debounce(() => this.handleScroll(), 30)
    );
  }

  disconnectedCallback(): void {
    window.removeEventListener('keydown', (e: KeyboardEvent) =>
      this.handleEscape(e)
    );
    window.removeEventListener(
      'scroll',
      debounce(() => this.handleScroll(), 30)
    );
    window.removeEventListener('click', (e: Event) =>
      this.windowClickAwayHandler(e)
    );
    this.stickyPositionObserver.disconnect();
  }

  firstUpdated() {
    const alertBody = document.querySelector(
      '[data-sitewide-alert]'
    ) as Element;
    if (alertBody) {
      this.stickyPositionObserver.observe(alertBody);
    }
    // get Drupal admin menu height from vars if present.
    this.setVars();
    this.resizeObserver.observe(this.main);
    // add custom event listener
    this.addEventListener('menuOpen', this.closeSearch);
  }

  render(): TemplateResult {
    const headerClasses = {
      'sic-main-header': true,
    };
    return html`
      <header @keydown=${this.handleEscape} class="${classMap(headerClasses)}">
        <sic-container
          class="sic-main-header--content-container"
          container-width=${this.isMobile ? 'full' : 'normal'}
          position-unset
          top-padding="none"
          bottom-padding="none"
        >
          ${this.isMobile ? this.mobileTemplate() : this.desktopTemplate()}
        </sic-container>
      </header>
      ${!this.isMobile && this.stickyMenu
        ? this.contentPositioningTemplate()
        : null}
    `;
  }

  desktopTemplate() {
    return html`
      <div class="sic-main-header-top-rail">
        <slot class="sic-main-header--logo" name="logo"></slot>
        <slot class="sic-main-header--nav" name="main-menu"></slot>
        <span class="sic-main-header--contact-log-wrapper">
          <slot class="sic-main-header--login" name="login"></slot>
          <slot class="sic-main-header--utility" name="desktop-utility"></slot>
        </span>
        <button
          class="btn secondary sic-main-header--search-button"
          aria-label="Toggle search"
          aria-expanded=${this.menuOpen}
          aria-haspopup="true"
          aria-controls="main-header-search-wrapper"
          @click=${this.menuButtonClickHandler}
        >
          <sic-icon
            name="search"
            size="24px"
            svg-fill="currentColor"
            aria-hidden="true"
          ></sic-icon>
        </button>
      </div>
      <slot class="sic-main-header--secondary" name="secondary" tabindex=${
        this.menuOpen ? -1 : null
      }></slot>
      <div class="sic-main-header--search-wrapper ${
        this.menuOpen ? 'open' : null
      }"
        id="main-header-search-wrapper"
      >
        <div class="divider"><hr></div>
        <span class="sic-main-header--search-close-wrapper">
        <button
        class="sic-main-header--search-close-button"
        @click=${this.menuButtonClickHandler}
        aria-label="Close search"
        aria-expanded=${this.menuOpen}
        >
        <sic-icon
              svg-fill="var(--color-primary-default)"
              name="times"
              size="24px"
              aria-hidden="true"
              >
            </button>
          </span>
          <slot class="sic-main-header--search" name="search"></slot>
        </div>
    `;
  }

  mobileTemplate() {
    return html`
        <div class="sic-main-header--top-rail-mobile">
          <button
          class="sic-main-header--menu-toggle-button-mobile"
          @click=${this.menuButtonClickHandler}
          aria-expanded=${!this.menuOpen ? 'no' : '' }
          aria-haspopup=${!this.menuOpen ? 'true' : 'false' }
          aria-controls="main-header-nav-search-mobile"
          aria-label=${!this.menuOpen ? 'open menu' : 'close menu' }
          >
            <sic-icon
              svg-color="var(--color-primary-default)"
              svg-fill="var(--color-primary-default)"
              name=${!this.menuOpen ? 'hamburger' : 'times-thin'}
              size=${!this.menuOpen ? '48px' : '40px'}
              aria-hidden="true"
            >
          </button>
          <slot class="sic-main-header--logo-mobile" name="logo"></slot>
        </div>
        <div class="sic-main-header--nav-search-mobile ${
          this.menuOpen ? 'open' : null
        }"
          id="main-header-nav-search-mobile"
        >
          <slot class="sic-main-header--search-mobile" name="search"></slot>
          <slot class="sic-main-header--nav-mobile" name="main-menu"></slot>
          <div class="sic-main-header--utility-wrapper-mobile">
            <slot class="sic-main-header--mobile-utility" name="mobile-utility"></slot>
          </div>
        </div>
    `;
  }

  /**
   *
   * @param elements
   * Toggles fixed class on header depending on scroll position.
   */
  toggleFixedClass(elements: IntersectionObserverEntry[]) {
    const self = this.shadowRoot?.querySelector(
      '.sic-main-header'
    ) as HTMLElement;
    const positioning = this.shadowRoot?.querySelector(
      '.sic-main-header--content-positioning '
    ) as HTMLElement;
    if (this.isMobile || !this.stickyMenu) {
      return;
    }
    elements.forEach(el => (this.alertHeight = el.target.clientHeight));

    elements.forEach(el =>
      el.isIntersecting
        ? (self.classList.remove('fixed'),
          positioning.classList.remove('fixed'))
        : (self.classList.add('fixed'), positioning.classList.add('fixed'))
    );
  }

  /**
   * Window handler to close open tabs and the search panel when the header is clicked away from.
   */
  windowClickAwayHandler(e: Event) {
    const clickedElement = e.target as HTMLElement;
    if (
      !this.isMobile &&
      clickedElement.nodeName !== 'SIC-NAV' &&
      clickedElement.nodeName !== 'SIC-ICON' &&
      clickedElement.nodeName !== 'LABEL' &&
      clickedElement.nodeName !== 'INPUT' &&
      clickedElement.nodeName !== 'SIC-MAIN-HEADER' &&
      !clickedElement.classList.contains('form-item')
    ) {
      this.cleanupOpenElements();
    }
    if (this.isMobile && this.menuOpen && clickedElement.nodeName === 'BODY') {
      this.cleanupOpenElements();
      this.checkBodyScrollLocked() ? this.removeBodyScrollLock() : null;
      this.checkMenuOpenScroll() ? this.removeMobileMenuScroll() : null;
      this.setFocusOnMobileClose();
    }
  }

  /**
   * Closes menus/search when switching between desktop/mobile
   */
  resetAtScreen() {
    if (
      this.isMobile !==
      window.innerWidth < (screenSizeMap.get('lg') as number)
    ) {
      this.cleanupOpenElements();
      this.checkBodyScrollLocked() ? this.removeBodyScrollLock() : null;
      this.checkMenuOpenScroll() ? this.removeMobileMenuScroll() : null;
      this.isMobile = window.innerWidth < (screenSizeMap.get('lg') as number);
    }
  }

  /**
   * Adds event listeners focus trap behavior to menu button and last utility link on mobile menu open.
   */
  applyFocusTrapEventListeners() {
    const utilityMenu = document
      .querySelector('[slot="mobile-utility"]')
      ?.querySelectorAll('a.icon-default.multi-line.sic-cta');
    const lastLink = utilityMenu?.[utilityMenu?.length - 1] as HTMLElement;
    const menuButton = this.shadowRoot?.querySelector(
      '.sic-main-header--menu-toggle-button-mobile'
    ) as HTMLElement;
    lastLink?.addEventListener('keydown', e =>
      this.focusTrapForward(e, menuButton)
    );
    menuButton.addEventListener('keydown', e =>
      this.focusTrapTabBack(e, lastLink)
    );
  }

  /**
   * Removes event listeners for focus trap behavior to menu button and last utility link on mobile menu close.
   */
  removeFocusTrapEventListeners() {
    const utilityMenu = document
      .querySelector('[slot="mobile-utility"]')
      ?.querySelectorAll('a.icon-default.multi-line.sic-cta');
    const lastLink = utilityMenu?.[utilityMenu?.length - 1] as HTMLElement;
    const menuButton = this.shadowRoot?.querySelector(
      '.sic-main-header--menu-toggle-button-mobile'
    ) as HTMLElement;
    lastLink?.removeEventListener('keydown', e =>
      this.focusTrapForward(e, menuButton)
    );
    menuButton?.removeEventListener('keydown', e =>
      this.focusTrapTabBack(e, lastLink)
    );
  }

  focusTrapForward(e: KeyboardEvent, targetElement: HTMLElement) {
    if (e.key !== 'Tab' || e.shiftKey) {
      //bail
      return;
    } else {
      e.preventDefault();
      targetElement.focus();
    }
  }

  focusTrapTabBack(e: KeyboardEvent, targetElement: HTMLElement) {
    if (!e.shiftKey || e.key !== 'Tab') {
      //bail
      return;
    }
    if (e.shiftKey && e.key === 'Tab') {
      e.preventDefault();
      targetElement.focus();
    }
  }

  /**
   * Updates this.scrollY and handles menu hide/show.
   */
  updateScrollY() {
    const scrolledDown = this.scrollY < window.scrollY;
    const scrolledUP = this.scrollY > window.scrollY;
    const fixedPositionCutoff = Math.max(this.alertHeight + 123);
    if (this.isMobile) {
      return;
    }

    if (
      (scrolledDown || this.haltScrollShow) &&
      window.scrollY > fixedPositionCutoff
    ) {
      this.style.setProperty('--top-position', this.menuHiddenPosition);
    }

    if (scrolledUP && !this.haltScrollShow) {
      this.style.setProperty('--top-position', '0px');
    }
    return (this.scrollY = window.scrollY);
  }

  handleScroll() {
    this.updateScrollY();
  }

  handleEscape(e: KeyboardEvent) {
    if (e.key !== 'Escape') {
      //bail
      return;
    } else {
      this.cleanupOpenElements();
      this.checkBodyScrollLocked() ? this.removeBodyScrollLock() : null;
      this.checkMenuOpenScroll() ? this.removeMobileMenuScroll() : null;
      this.setFocusOnMobileClose();
    }
  }

  closeSearch() {
    if (this.menuOpen) {
      this.menuOpen = !this.menuOpen;
    }
  }

  setVars() {
    this.style.setProperty('--top-position', '0px');
    //set initial value, if secondary header content
    //the mainmenu-secondary block will override this value.
    this.style.setProperty('--has-secondary-content', `123px`);
    this.style.setProperty(
      '--content-positioning-height',
      'var(--has-secondary-content)'
    );
  }

  cleanupOpenElements() {
    this.cleanupMobileOnClose();
    this.closeSearch();
  }

  /**
   * Places relative positioned div "behind" main nav, to force content below.
   * @returns Template
   */
  contentPositioningTemplate() {
    return html` <div class="sic-main-header--content-positioning"></div> `;
  }

  menuButtonClickHandler() {
    this.menuOpen = !this.menuOpen;
    this.cleanupMobileOnClose();

    // For mobile only below
    if (!this.isMobile) {
      // bail
      return;
    }
    if (this.stickyMenu) {
      this.toggleScrollLock();
      this.toggleMobileMenuScroll();
    }
    if (this.menuOpen) {
      this.applyFocusTrapEventListeners();
      this.forceFocusOnMobileOpen();
    } else {
      this.removeFocusTrapEventListeners();
    }
  }

  forceFocusOnMobileOpen() {
    const input = this.querySelector('sic-search')?.querySelector(
      '#header-search-query'
    ) as HTMLInputElement;
    if (input) {
      // Give the DOM a moment to load before establishing focus by using setTimeout, running asynchronously, after the shortest possible delay
      setTimeout(() => {
        input.focus();
      }, 0);
    }
  }

  setFocusOnMobileClose() {
    const hamburger = this.shadowRoot?.querySelector(
      '.sic-main-header--menu-toggle-button-mobile'
    ) as HTMLButtonElement;
    hamburger.focus();
  }

  toggleScrollLock() {
    const bodyLocked = this.checkBodyScrollLocked();
    if (bodyLocked) {
      return this.removeBodyScrollLock();
    } else {
      return document.body.classList.add('mobile-scroll-locked');
    }
  }

  toggleMobileMenuScroll() {
    const menuOpenScroll = this.checkMenuOpenScroll();
    if (menuOpenScroll) {
      return this.removeMobileMenuScroll();
    } else {
      return this.classList.add('mobile-open-scroll');
    }
  }

  checkMenuOpenScroll() {
    return this.classList.contains('mobile-open-scroll');
  }

  checkBodyScrollLocked() {
    return document.body.classList.contains('mobile-scroll-locked');
  }

  removeBodyScrollLock() {
    return document.body.classList.remove('mobile-scroll-locked');
  }

  removeMobileMenuScroll() {
    return this.classList.remove('mobile-open-scroll');
  }

  /**
   * Removes 'open' attribute and '.flyaway' class.
   */
  cleanupMobileOnClose() {
    const children = [
      // @ts-expect-error because
      ...this.mainMenu[0].shadowRoot?.querySelectorAll('[open]'),
      ...this.mainMenu[0].shadowRoot?.querySelectorAll('.flyaway'),
    ];

    if (children && children.length) {
      Array.from(children).forEach(child => {
        if (child.hasAttribute('open')) {
          if (child.nodeName === 'SIC-FLYOUT') {
            const engagedEl = child as SicFlyout;
            engagedEl.toggleMenu();
          }

          if (child.nodeName === 'SIC-NAV-ITEM') {
            const engagedEl = child as SicNavItem;
            engagedEl.open = false;
            engagedEl.clearChildStatus();
            child.removeAttribute('open');
          }
        }
        if (child.classList.contains('flyaway')) {
          child.classList.remove('flyaway');
        }
      });
    }
    this.style.removeProperty('--mobile-utility-offset');
  }
}
